import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Building2, Users, UserCog, ClipboardList, History, LogOut, Menu, X, GraduationCap, Briefcase, BookOpen, BarChart3, Newspaper, Award, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('token');
        navigate('/login');
      });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (!user) return <div className="flex items-center justify-center h-screen">Cargando...</div>;

  const isActive = (path) => location.pathname === path;

  const adminLinks = [
    { path: '/dashboard', icon: BarChart3, label: 'Dashboard' },
    { path: '/admin/departments', icon: Building2, label: 'Departamentos' },
    { path: '/admin/employees', icon: Users, label: 'Empleados' },
    { path: '/admin/guards', icon: UserCog, label: 'Vigilantes' },
    { path: '/admin/projects', icon: Briefcase, label: 'Proyectos' },
    { path: '/admin/personal', icon: Users, label: 'Personal' },
    { path: '/admin/intranet', icon: Newspaper, label: 'Intranet' },
    { path: '/admin/courses', icon: BookOpen, label: 'Cursos' },
    { path: '/admin/training-reports', icon: BarChart3, label: 'Reportes Capacitación' },
    { path: '/admin/sistema', icon: Database, label: 'Sistema · Respaldo' },
  ];

  const guardLinks = [
    { path: '/dashboard', icon: BarChart3, label: 'Dashboard' },
    { path: '/guard/pending', icon: ClipboardList, label: 'Aprobaciones' },
  ];

  const personalLinks = [
    { path: '/intranet', icon: Newspaper, label: 'Intranet' },
    { path: '/academia', icon: GraduationCap, label: 'Mi Academia' },
    { path: '/mis-diplomas', icon: Award, label: 'Mis Diplomas' },
  ];

  const commonLinks = [
    { path: '/history', icon: History, label: 'Historial Visitas' },
  ];

  let links;
  if (user.role === 'admin') {
    links = [...adminLinks, ...commonLinks];
  } else if (user.role === 'personal') {
    links = [...personalLinks];
  } else {
    links = [...guardLinks, ...commonLinks];
  }

  const roleLabel = user.role === 'admin' ? 'Admin' : user.role === 'personal' ? 'Personal' : 'Vigilante';
  // Header y logo unificados (mismo tamaño que en formulario público de Visitantes)
  const headerHeight = 'h-20';
  const sidebarTop = 'top-20 h-[calc(100vh-5rem)]';
  const logoClass = 'h-12 md:h-16';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className={`flex items-center justify-between px-4 ${headerHeight}`}>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden"
              data-testid="menu-toggle-button"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Link to={user.role === 'personal' ? '/intranet' : '/'} className="flex items-center gap-3">
              <img src="/assets/logo.jpg" alt="Proesa" className={logoClass} />
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600 hidden sm:inline">
              {user.name} <span className="text-xs text-gray-400">({roleLabel})</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 text-gray-600 hover:text-[#DC143C]"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed md:sticky ${sidebarTop} left-0 w-64 bg-[#1F2937] text-white transition-transform duration-300 z-20 overflow-y-auto ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          }`}
        >
          <nav className="p-4 space-y-2">
            {links.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    isActive(link.path)
                      ? 'bg-[#DC143C] text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium uppercase tracking-wide text-sm">{link.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};
